import { useEffect, useRef, useState, useMemo } from "react";
import {
  MicOff, Pause, Users, PhoneForwarded, PhoneOff, Send, Paperclip, Bot,
  Tag, Percent, CalendarClock, AlertOctagon, Phone, MessageCircle, UserPlus,
  LayoutTemplate, Plus, Trash2, Loader2, Check, CheckCheck, AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { channelMeta, fmtTime, initials } from "@/lib/app-utils";
import CreateLeadFromConvDialog from "@/components/CreateLeadFromConvDialog";
import LiveCallControlBar from "@/components/LiveCallControlBar";
import { useCallTimer } from "@/hooks/useCallTimer";

// Split an email body into the new reply text vs. the quoted history beneath it
// so long threads stay readable (the quote collapses behind a toggle).
function splitQuoted(body = "") {
  const lines = body.split("\n");
  let idx = -1;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i].trim();
    if (/^On .+wrote:?$/i.test(l) ||
        /^-{2,}\s*Original Message\s*-{2,}$/i.test(l) ||
        /^_{5,}$/.test(l) ||
        (i > 0 && /^From:\s/i.test(l))) { idx = i; break; }
    if (l.startsWith(">")) { idx = i; break; }
  }
  if (idx <= 0) return { main: body.trim(), quoted: "" };
  return {
    main: lines.slice(0, idx).join("\n").trim(),
    quoted: lines.slice(idx).join("\n").trim(),
  };
}

// A readable email message card (replaces chat bubbles for the Email channel).
const EmailCard = ({ m, contactName }) => {
  const [showQuoted, setShowQuoted] = useState(false);
  const isAgent = m.from === "agent";
  const { main, quoted } = splitQuoted(m.body || "");
  return (
    <div
      data-testid="email-card"
      className={`rounded-xl border bg-card shadow-sm overflow-hidden ${isAgent ? "border-primary/30" : "border-border"}`}
    >
      <div className={`flex items-center gap-2.5 px-4 py-2.5 border-b ${isAgent ? "bg-primary/5 border-primary/15" : "bg-muted/40 border-border"}`}>
        <span className={`w-7 h-7 rounded-full grid place-items-center text-[10px] font-bold shrink-0 ${isAgent ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
          {isAgent ? "You" : initials(contactName)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold truncate">{isAgent ? "You (Agent)" : contactName}</div>
          <div className="text-[10px] text-muted-foreground">{isAgent ? "Sent" : "Received"}</div>
        </div>
        <div className="text-[10px] text-muted-foreground shrink-0">{fmtTime(m.ts)}</div>
      </div>
      <div className="px-4 py-3 space-y-2 min-w-0">
        {m.subject && <div className="font-semibold text-sm break-words">{m.subject}</div>}
        <div className="text-sm leading-relaxed whitespace-pre-wrap break-words text-foreground/90">
          {main || "(no content)"}
        </div>
        {quoted && (
          <div>
            <button
              data-testid="email-toggle-quoted"
              onClick={() => setShowQuoted((v) => !v)}
              className="text-xs text-primary hover:underline"
            >
              {showQuoted ? "Hide quoted text" : "Show quoted text"}
            </button>
            {showQuoted && (
              <div className="mt-2 pl-3 border-l-2 border-border text-xs leading-relaxed whitespace-pre-wrap break-words text-muted-foreground">
                {quoted}
              </div>
            )}
          </div>
        )}
        {(m.attachments || []).map((a, i) => (
          <a key={i} href={a.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] underline opacity-80 mt-1">
            <Paperclip className="w-3 h-3" />{a.type || "attachment"}
          </a>
        ))}
      </div>
    </div>
  );
};

export default function ConversationView({ conversation, onSent, onCall = false, aiCopilot = false, otherChannels = [] }) {
  const [drafts, setDrafts] = useState({});   // per-channel composer drafts {channel: text}
  const [tone, setTone] = useState("Friendly Tone");
  const [activeChannel, setActiveChannel] = useState(conversation?.primary_channel || "voice");
  // Composer text is SCOPED to the active channel so switching channels
  // (WhatsApp ↔ Instagram ↔ …) never leaks one channel's draft into another.
  const text = drafts[activeChannel] || "";
  const setText = (updater) =>
    setDrafts((d) => {
      const cur = d[activeChannel] || "";
      const next = typeof updater === "function" ? updater(cur) : updater;
      return { ...d, [activeChannel]: next };
    });
  const [showCreateLead, setShowCreateLead] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [calling, setCalling] = useState(false);
  const { mmss: callMmss, active: callTimerActive } = useCallTimer(conversation?.call_status === "dead");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [liveChannels, setLiveChannels] = useState([]);   // enabled+configured channels
  const draftAppliedRef = useRef(null);
  const lastConvRef = useRef(null);
  const sendingRef = useRef(false);
  // Ghost drafting + per-conversation draft lifecycle in ONE effect so the
  // reset never wipes the freshly-applied draft. On a NEW conversation we clear
  // all per-channel drafts; then we pre-fill the bot's suggested reply into the
  // composer for the conversation's OWN primary channel only (never leaking it
  // onto a cross-channel thread the agent switched to).
  useEffect(() => {
    if (!conversation?.id) return;
    if (lastConvRef.current !== conversation.id) {
      lastConvRef.current = conversation.id;
      draftAppliedRef.current = null;
      setDrafts({});
    }
    const d = conversation?.bot_draft;
    if (!d) return;
    // When the AI Copilot is ON it is the single source of AI replies — do NOT
    // also ghost-fill the composer with the gatekeeper bot draft (that created
    // two mismatched suggestions). Copilot-off keeps the composer pre-fill.
    if (aiCopilot) return;
    if (activeChannel !== conversation.primary_channel) return;
    if (draftAppliedRef.current === conversation.id) return;
    draftAppliedRef.current = conversation.id;
    if (conversation.primary_channel === "email") {
      setEmailBody((v) => v || d);
    } else {
      const ch = conversation.primary_channel;
      setDrafts((prev) => ({ ...prev, [ch]: prev[ch] || d }));
    }
  }, [conversation?.id, conversation?.bot_draft, activeChannel, aiCopilot]);

  // AI Copilot → "Insert into reply": drop the suggested text into THIS
  // channel's composer (or the email body) so the agent can edit before send.
  useEffect(() => {
    const onInsert = (e) => {
      const { channel, text: t } = e?.detail || {};
      if (!t) return;
      const ch = channel || activeChannel;
      // Email can render as either the history compose form (emailBody) or the
      // generic channel composer (drafts.email) — populate both so the inserted
      // reply is always visible wherever the email composer surfaces.
      if (ch === "email") setEmailBody(t);
      setDrafts((prev) => ({ ...prev, [ch]: t }));
    };
    window.addEventListener("copilot:insert", onInsert);
    return () => window.removeEventListener("copilot:insert", onInsert);
  }, [activeChannel]);
  const [hist, setHist] = useState({});                   // {channel: {loading,messages,error,meta}}
  const endRef = useRef(null);

  const contactId = conversation?.contact_id || null;

  // Load the tenant's LIVE channels once (enabled + configured in the Hub).
  // Refresh when an admin toggles/reconfigures a channel so newly-enabled
  // channels (e.g. Telegram) appear in the switcher without a reload.
  useEffect(() => {
    const load = () => api.get("/me/channels")
      .then((r) => setLiveChannels(r.data?.channels || []))
      .catch(() => setLiveChannels([{ channel: "voice", label: "Voice", supports_history: false }]));
    load();
    const onCfg = () => load();
    window.addEventListener("channel:config", onCfg);
    return () => window.removeEventListener("channel:config", onCfg);
  }, []);

  useEffect(() => {
    setActiveChannel(conversation?.primary_channel || "voice");
    setHist({});
    setLiveThread(null);
    setThreadMsgs(null);
    setStatusOverrides({});
  }, [conversation?.id]);

  // Live delivery ticks: apply Viber delivered/seen/failed receipts to the open
  // conversation's outbound messages (matched by external_id / message_token).
  useEffect(() => {
    const onStatus = (e) => {
      const d = e?.detail || {};
      if (!d.conversation_id || d.conversation_id !== conversation?.id) return;
      if (!d.message_external_id || !d.status) return;
      setStatusOverrides((prev) => ({ ...prev, [d.message_external_id]: d.status }));
    };
    window.addEventListener("message:status", onStatus);
    return () => window.removeEventListener("message:status", onStatus);
  }, [conversation?.id]);

  const [liveThread, setLiveThread] = useState(null);     // {loading,conv,reason,error} for in-call live chat
  const [threadMsgs, setThreadMsgs] = useState(null);     // real-time refreshed messages for the open PP thread
  const [statusOverrides, setStatusOverrides] = useState({}); // {external_id: delivery_status} live delivery-receipt ticks
  const historyChannels = useMemo(
    () => new Set(liveChannels.filter((c) => c.supports_history).map((c) => c.channel)),
    [liveChannels]
  );
  const liveChatChannels = useMemo(
    () => new Set(liveChannels.filter((c) => c.supports_live_chat).map((c) => c.channel)),
    [liveChannels]
  );
  // Channels that are LIVE (enabled + configured in the Hub) → show a connected dot.
  const connectedChannels = useMemo(
    () => new Set([...liveChannels.map((c) => c.channel), "voice"]),
    [liveChannels]
  );
  const isHistoryChannel = historyChannels.has(activeChannel);
  const isPPLive = liveChatChannels.has(activeChannel);   // Instagram/FB/Telegram via PostProxy
  const ownChannel = activeChannel === conversation?.primary_channel;
  const isLiveCall = conversation?.live_call === true;
  // A stored PostProxy conversation opened straight from the inbox.
  const ownStored = ownChannel && !isLiveCall &&
    (conversation?.source === "postproxy" || conversation?.source === "wati" || conversation?.source === "viber");
  // PostProxy / WATI channel that needs an editable thread resolved (in-call, or peeking).
  const needsThreadLoad = isPPLive && !ownStored;
  // Email is the only read-only history channel.
  const isEmailHistory = isHistoryChannel && !isPPLive;
  // A WhatsApp conversation backed by WATI (enables template sends).
  const isWati = activeChannel === "whatsapp" &&
    (conversation?.source === "wati" || liveThread?.conv?.source === "wati");
  // The bot's suggested draft only makes sense on the conversation's OWN
  // primary channel — not on a cross-channel thread the agent peeked into.
  // When the AI Copilot is ON, the Copilot panel is the single AI-reply source,
  // so the composer bot-draft banner is suppressed (no duplicate suggestions).
  const showBotDraft = !!conversation?.bot_draft && ownChannel && !needsThreadLoad && !aiCopilot;

  // Email read-only history fetch.
  useEffect(() => {
    if (!isEmailHistory || !contactId) return;
    if (hist[activeChannel] && !hist[activeChannel].error) return;  // cached
    setHist((h) => ({ ...h, [activeChannel]: { loading: true, messages: [] } }));
    api.get("/me/channel-history", { params: { channel: activeChannel, contact_id: contactId } })
      .then((r) => setHist((h) => ({ ...h, [activeChannel]: { loading: false, messages: r.data?.messages || [], meta: r.data?.meta } })))
      .catch((e) => setHist((h) => ({ ...h, [activeChannel]: { loading: false, messages: [], error: formatErr(e) } })));
    // eslint-disable-next-line
  }, [activeChannel, contactId, isEmailHistory]);

  // Prefill the reply subject with "Re: <latest subject>" once history loads.
  useEffect(() => {
    if (!isEmailHistory) return;
    setEmailSubject((cur) => {
      if (cur) return cur;
      const msgs = hist[activeChannel]?.messages || [];
      const lastSubj = [...msgs].reverse().find((m) => m.subject)?.subject;
      if (!lastSubj) return cur;
      return /^re:/i.test(lastSubj) ? lastSubj : `Re: ${lastSubj}`;
    });
    // eslint-disable-next-line
  }, [isEmailHistory, activeChannel, hist[activeChannel]?.messages]);

  // Resolve the editable PostProxy/WATI thread for the live chat. Re-runs when
  // the CRM handle is edited (c7:contact-updated) so a just-entered WhatsApp
  // number immediately loads the matching conversation.
  const [threadNonce, setThreadNonce] = useState(0);
  useEffect(() => {
    const onUpd = (e) => {
      if (!contactId || (e?.detail?.contact_id && e.detail.contact_id !== contactId)) return;
      setThreadNonce((n) => n + 1);
    };
    window.addEventListener("c7:contact-updated", onUpd);
    return () => window.removeEventListener("c7:contact-updated", onUpd);
  }, [contactId]);
  useEffect(() => {
    if (!needsThreadLoad || !contactId) { setLiveThread(null); return; }
    setLiveThread({ loading: true });
    api.get("/me/channel-thread", { params: { channel: activeChannel, contact_id: contactId } })
      .then((r) => setLiveThread({ loading: false, conv: r.data?.conversation, reason: r.data?.reason }))
      .catch((e) => setLiveThread({ loading: false, error: formatErr(e) }));
    // eslint-disable-next-line
  }, [activeChannel, contactId, needsThreadLoad, threadNonce]);

  // The active PostProxy conversation id (stored-from-inbox OR resolved thread).
  const ppConvId = ownStored ? conversation?.id : (needsThreadLoad ? liveThread?.conv?.id : null);

  // Near-real-time refresh of the OPEN PostProxy thread: poll every 8s + on any
  // inbound WS event for this conversation.
  useEffect(() => {
    setThreadMsgs(null);
    if (!ppConvId) return;
    let stopped = false;
    const refresh = async () => {
      try {
        const r = await api.post(`/conversations/${ppConvId}/sync`);
        if (!stopped) setThreadMsgs(r.data?.conversation?.messages || []);
      } catch { /* ignore transient */ }
    };
    refresh();
    const iv = setInterval(refresh, 8000);
    const onInbound = (e) => { if (e?.detail?.conversation_id === ppConvId) refresh(); };
    window.addEventListener("channel:inbound", onInbound);
    return () => { stopped = true; clearInterval(iv); window.removeEventListener("channel:inbound", onInbound); };
  }, [ppConvId]);

  // Unread counts per channel (from the unified inbox summary) so the agent
  // sees "you also have unread on WhatsApp / Email / …" when they open a
  // customer, and can jump to that channel from the switcher.
  const unreadByChannel = useMemo(() => {
    const map = {};
    (otherChannels || []).forEach((x) => {
      if (x && x.channel && x.unread) map[x.channel] = (map[x.channel] || 0) + x.unread;
    });
    return map;
  }, [otherChannels]);
  const otherUnread = useMemo(
    () => (otherChannels || []).filter((x) => x && x.unread && x.channel !== activeChannel),
    [otherChannels, activeChannel]
  );

  // Channels to render in the switcher: all live channels, plus the current
  // conversation's primary channel if it isn't already live (so it's reachable).
  const switcherChannels = useMemo(() => {
    const list = liveChannels.map((c) => c.channel);
    const pc = conversation?.primary_channel;
    if (pc && pc !== "system" && !list.includes(pc)) list.unshift(pc);
    if (!list.includes("voice")) list.unshift("voice");
    return list;
  }, [liveChannels, conversation?.primary_channel]);

  // Filter messages by selected channel (system messages always shown)
  const filteredMessages = useMemo(() => {
    if (!conversation?.messages) return [];
    return conversation.messages.filter((m) => m.from === "system" || m.channel === activeChannel);
  }, [conversation?.messages, activeChannel]);

  const histEntry = hist[activeChannel];
  // What to render + whether the composer is live.
  let shownMessages;
  if (isEmailHistory) shownMessages = histEntry?.messages || [];
  else if (ppConvId) shownMessages = threadMsgs ?? (ownStored ? conversation.messages : liveThread?.conv?.messages) ?? [];
  else shownMessages = filteredMessages;
  const sendTargetId = ppConvId || conversation?.id;
  const readOnly = isEmailHistory || (needsThreadLoad && !liveThread?.conv);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [shownMessages.length, activeChannel]);

  // Tell the AI Copilot which channel the agent is currently viewing so it can
  // immediately re-read that channel's thread and regenerate its suggested
  // reply (issue: switching channels must refocus the copilot).
  useEffect(() => {
    if (!conversation?.id) return;
    window.dispatchEvent(new CustomEvent("copilot:context", {
      detail: {
        conversation_id: sendTargetId,
        channel: activeChannel,
        messages: (shownMessages || []).map((m) => ({
          from: m.from || "customer", body: m.body || "",
        })),
        read_only: readOnly,
      },
    }));
    // eslint-disable-next-line
  }, [conversation?.id, activeChannel, sendTargetId, shownMessages.length, readOnly]);

  if (!conversation) {
    return (
      <div className="h-full grid place-items-center">
        <div className="text-center max-w-sm px-6">
          <div className="mx-auto w-20 h-20 rounded-2xl bg-muted grid place-items-center mb-4">
            <MessageCircle className="w-9 h-9 text-muted-foreground" strokeWidth={1.5} />
          </div>
          <h3 className="font-display text-xl font-bold">Select a customer from the inbox</h3>
          <p className="text-sm text-muted-foreground mt-1">Pick a conversation to start viewing the timeline and live call.</p>
        </div>
      </div>
    );
  }

  async function send() {
    if (!text.trim() || !sendTargetId) return;
    if (sendingRef.current) return;   // guard: ignore repeat Enter/click while a send is in flight
    sendingRef.current = true;
    const body = text;
    setText("");   // clear immediately so rapid Enter presses can't re-capture the same text
    try {
      const { data } = await api.post(`/conversations/${sendTargetId}/messages`, { body, channel: activeChannel });
      // Optimistically reflect the sent message in the open PostProxy thread.
      if (ppConvId) setThreadMsgs((prev) => [...(prev ?? shownMessages ?? []), data]);
      onSent?.(data);
      toast.success(`Sent via ${channelMeta[activeChannel]?.label || activeChannel}`);
    } catch (e) {
      setText((cur) => (cur ? cur : body));   // restore for retry only if the agent hasn't typed something new
      toast.error(formatErr(e) || "Failed to send");
    } finally {
      sendingRef.current = false;
    }
  }

  async function sendEmail() {
    if (!emailBody.trim() || !contactId) return;
    setEmailSending(true);
    try {
      const msgs = hist[activeChannel]?.messages || [];
      const lastInbound = [...msgs].reverse().find((m) => m.from === "customer");
      const { data } = await api.post("/me/send-email", {
        contact_id: contactId,
        subject: emailSubject,
        body: emailBody,
        in_reply_to: lastInbound?.id || null,
      });
      setHist((h) => ({
        ...h,
        [activeChannel]: {
          ...(h[activeChannel] || {}),
          messages: [...((h[activeChannel] || {}).messages || []), data.message],
        },
      }));
      setEmailBody("");
      toast.success("Email sent");
    } catch (e) {
      toast.error(formatErr(e) || "Failed to send email");
    } finally {
      setEmailSending(false);
    }
  }

  // Place a real outbound voice call to the customer. Routes through the SAME
  // manual-dial flow as the CRM panel (`c7:dial-number`) so it's gated by the
  // header campaign, labeled "Manual", and recorded — this is what lets an
  // agent dial straight from an omni-inbox chat exactly like a manual dial.
  async function placeCall() {
    if (!conversation) return;
    // Try the linked contact's phone first; fall back to a numeric-looking primary key
    let phone = "";
    if (conversation.contact_id) {
      try {
        const { data: contact } = await api.get(`/contacts/${conversation.contact_id}`);
        phone = (contact?.phone || "").trim();
      } catch { /* ignore */ }
    }
    if (!phone) {
      // Fall back to conversation contact_name handle if it looks like a phone
      const guess = (conversation.preview || "").match(/\+?\d[\d\s\-()]{6,}/);
      phone = guess ? guess[0].trim() : "";
    }
    if (!phone) {
      toast.error("No phone number on file. Create a CRM contact first to enable voice calls.");
      return;
    }
    setCalling(true);
    try {
      window.dispatchEvent(new CustomEvent("c7:dial-number", {
        detail: { phone, name: conversation.contact_name || "" },
      }));
      toast.success(`Dialing ${conversation.contact_name || phone}…`);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setCalling(false);
    }
  }

  const QuickAction = ({ icon: Icon, label, color, dataId }) => (
    <button
      data-testid={dataId}
      onClick={() => toast(`${label} — coming soon`)}
      className="flex items-center gap-1.5 h-8 px-3 rounded-md bg-muted/60 hover:bg-muted border border-border text-xs whitespace-nowrap"
    >
      <Icon className={`w-3.5 h-3.5 ${color}`} />
      {label}
    </button>
  );

  const ViberTick = ({ m, override }) => {
    const status = override || m.delivery_status || "sent";
    const map = {
      sent:      { Icon: Check,       cls: "text-muted-foreground",       label: "Sent" },
      delivered: { Icon: CheckCheck,  cls: "text-muted-foreground",       label: "Delivered" },
      seen:      { Icon: CheckCheck,  cls: "text-sky-500",                label: "Seen" },
      failed:    { Icon: AlertCircle, cls: "text-red-500",                label: "Failed" },
      send_failed:{Icon: AlertCircle, cls: "text-red-500",                label: "Failed" },
    };
    const s = map[status] || map.sent;
    const Icon = s.Icon;
    return (
      <span className={`inline-flex items-center ${s.cls}`} title={s.label}
            data-testid={`viber-tick-${status}`}>
        <Icon className="w-3 h-3" strokeWidth={2.5} />
      </span>
    );
  };

  const Bubble = ({ m }) => {
    const ch = channelMeta[m.channel] || channelMeta.system;
    if (m.from === "system") {
      return (
        <div className="flex items-center gap-2 text-xs text-muted-foreground px-2 py-2">
          <Bot className="w-3.5 h-3.5" />
          <span className="italic">{m.body}</span>
        </div>
      );
    }
    const isAgent = m.from === "agent";
    return (
      <div className={`flex ${isAgent ? "justify-end" : "justify-start"}`}>
        <div className={`max-w-[80%] ${isAgent ? "items-end" : "items-start"} flex flex-col gap-1`}>
          <div
            className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm whitespace-pre-wrap ${
              isAgent ? "bg-secondary text-secondary-foreground rounded-br-md" : "bg-card border border-border rounded-bl-md"
            }`}
          >
            {m.subject && <div className="font-semibold text-xs mb-1 opacity-90">{m.subject}</div>}
            {m.body}
            {(m.attachments || []).map((a, i) => (
              <a key={i} href={a.url} target="_blank" rel="noreferrer" className="block text-[11px] underline mt-1 opacity-80">
                📎 {a.type || "attachment"}
              </a>
            ))}
          </div>
          <div className="text-[10px] text-muted-foreground flex items-center gap-1">
            <span>{fmtTime(m.ts)}</span>
            {isAgent && m.channel === "viber" && <ViberTick m={m} override={statusOverrides[m.external_id]} />}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full min-w-0">
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b border-border">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <Avatar className="h-11 w-11"><AvatarFallback className="bg-secondary text-secondary-foreground font-bold">{initials(conversation.contact_name)}</AvatarFallback></Avatar>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display text-lg font-bold tracking-tight truncate">{conversation.contact_name}</h3>
                {conversation.live_call && (
                  conversation.call_status === "dead" ? (
                    <Badge data-testid="conv-dead-call-badge" variant="outline" className="border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400 gap-1.5">
                      <span className="relative inline-block w-1.5 h-1.5 rounded-full bg-red-500" /> Dead call
                      {callTimerActive && <span data-testid="conv-call-timer" className="font-mono tabular-nums font-semibold">{callMmss}</span>}
                    </Badge>
                  ) : (
                    <Badge data-testid="conv-live-call-badge" variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 gap-1.5">
                      <span className="relative inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 text-emerald-500 live-pulse" /> Live call
                      {callTimerActive && <span data-testid="conv-call-timer" className="font-mono tabular-nums font-semibold">{callMmss}</span>}
                    </Badge>
                  )
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 truncate">
                {channelMeta[conversation.primary_channel]?.label || ""}
                {conversation.contact_id ? " · Customer since 2021" : " · New lead — not in CRM"}
              </div>
            </div>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap">
            {!conversation.contact_id && (
              <Button
                data-testid="header-create-lead-btn"
                onClick={() => setShowCreateLead(true)}
                variant="outline"
                className="rounded-full gap-2 shrink-0 border-primary/30 text-primary hover:bg-primary/10"
                title="Convert this lead into a CRM contact"
              >
                <UserPlus className="w-4 h-4" /> Create Contact
              </Button>
            )}
            <Button
              data-testid="header-call-btn"
              onClick={placeCall}
              disabled={calling || onCall}
              title={onCall ? "You're already on a call" : "Call this customer"}
              className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground gap-2 shrink-0 disabled:opacity-50"
            >
              <Phone className="w-4 h-4" /> {calling ? "Dialing…" : "Call"}
            </Button>
          </div>
        </div>

        {/* Channel switcher (filters thread) — only LIVE (enabled+configured) channels */}
        <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="conv-channel-row">
          <span className="text-overline text-muted-foreground mr-1">Channel</span>
          {switcherChannels.map((c) => {
            const ch = channelMeta[c] || channelMeta.system;
            const Icon = ch.Icon;
            const active = activeChannel === c;
            const supportsHist = historyChannels.has(c);
            const connected = connectedChannels.has(c);
            return (
              <Tooltip key={c}>
                <TooltipTrigger asChild>
                  <button
                    data-testid={`channel-${c}`}
                    onClick={() => setActiveChannel(c)}
                    className={`relative w-9 h-9 rounded-full grid place-items-center text-white ${ch.cls} transition-all hover:scale-105 ${active ? "ring-2 ring-offset-2 ring-offset-background ring-primary scale-105" : ""}`}
                  >
                    <Icon className="w-4 h-4" />
                    {connected && (
                      <span
                        data-testid={`channel-status-${c}`}
                        className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-card"
                        title="Connected"
                      />
                    )}
                    {!active && unreadByChannel[c] ? (
                      <span
                        data-testid={`channel-unread-${c}`}
                        className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 grid place-items-center rounded-full bg-red-500 text-white text-[9px] font-bold ring-2 ring-card"
                      >
                        {unreadByChannel[c]}
                      </span>
                    ) : null}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {ch.label}
                  {connected ? " · connected" : ""}
                  {supportsHist ? " · click for history" : ""}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        {otherUnread.length > 0 && (
          <div
            data-testid="conv-other-channels-strip"
            className="mt-2 flex items-center gap-2 flex-wrap text-[11px] rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 px-2.5 py-1.5"
          >
            <span className="text-amber-700 dark:text-amber-300 font-medium">Also unread:</span>
            {otherUnread.map((x) => {
              const cm = channelMeta[x.channel] || channelMeta.system;
              const CI = cm.Icon;
              return (
                <button
                  key={x.channel}
                  data-testid={`conv-other-channel-${x.channel}`}
                  onClick={() => setActiveChannel(x.channel)}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/70 dark:bg-white/10 border border-amber-200 dark:border-amber-800 hover:bg-white"
                >
                  <CI className="w-3 h-3" /> {cm.label}
                  <span className="ml-0.5 min-w-[14px] h-3.5 px-1 grid place-items-center rounded-full bg-red-500 text-white text-[8px] font-bold">{x.unread}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Call controls */}
        {conversation.live_call && (
          <LiveCallControlBar className="mt-4" aiMonitoring={aiCopilot} />
        )}
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="px-5 py-5 space-y-4">
          <div className="text-overline text-muted-foreground text-center flex items-center justify-center gap-2">
            <span className={`w-3.5 h-3.5 rounded grid place-items-center ${(channelMeta[activeChannel] || channelMeta.system).cls}`}>
              {(() => { const I = (channelMeta[activeChannel] || channelMeta.system).Icon; return <I className="w-2.5 h-2.5" />; })()}
            </span>
            {(channelMeta[activeChannel] || channelMeta.system).label}
            {isEmailHistory ? " · history with this customer" : (isPPLive ? " · live chat" : " · Today")}
          </div>

          {(isEmailHistory || needsThreadLoad) && !contactId && (
            <div className="text-center text-xs text-muted-foreground py-10" data-testid="history-no-contact">
              Save this caller as a CRM contact to load their {(channelMeta[activeChannel] || {}).label || activeChannel} {isPPLive ? "chat" : "history"}.
            </div>
          )}

          {/* Email read-only history states */}
          {isEmailHistory && contactId && histEntry?.loading && (
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground py-10" data-testid="history-loading">
              <span className="w-3.5 h-3.5 rounded-full border-2 border-muted-foreground/30 border-t-primary animate-spin" />
              Loading {(channelMeta[activeChannel] || {}).label || activeChannel} history…
            </div>
          )}
          {isEmailHistory && contactId && !histEntry?.loading && histEntry?.error && (
            <div className="text-center text-xs text-amber-600 dark:text-amber-400 py-10 px-6" data-testid="history-error">
              {histEntry.error}
            </div>
          )}
          {isEmailHistory && contactId && !histEntry?.loading && !histEntry?.error && shownMessages.length === 0 && (
            <div className="text-center text-xs text-muted-foreground py-10" data-testid="history-empty">
              No {(channelMeta[activeChannel] || {}).label || activeChannel} history found with this customer.
            </div>
          )}

          {/* PostProxy live-chat thread states (in-call / peek) */}
          {needsThreadLoad && contactId && liveThread?.loading && (
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground py-10" data-testid="thread-loading">
              <span className="w-3.5 h-3.5 rounded-full border-2 border-muted-foreground/30 border-t-primary animate-spin" />
              Opening {(channelMeta[activeChannel] || {}).label || activeChannel} chat…
            </div>
          )}
          {needsThreadLoad && contactId && !liveThread?.loading && liveThread?.error && (
            <div className="text-center text-xs text-amber-600 dark:text-amber-400 py-10 px-6" data-testid="thread-error">
              {liveThread.error}
            </div>
          )}
          {needsThreadLoad && contactId && !liveThread?.loading && !liveThread?.conv && liveThread?.reason === "no_handle" && (
            <div className="text-center text-xs text-muted-foreground py-10 px-6" data-testid="thread-no-handle">
              {activeChannel === "whatsapp"
                ? <>Add the customer's <strong>WhatsApp number / ID</strong> in the <strong>Whatsapp_handler</strong> field (CRM Details) to see the WhatsApp chat.</>
                : <>Add this customer's {(channelMeta[activeChannel] || {}).label || activeChannel} handle in <strong>CRM Details</strong> to start live chatting.</>}
            </div>
          )}
          {needsThreadLoad && contactId && !liveThread?.loading && !liveThread?.conv && liveThread?.reason === "no_chat" && (
            <div className="text-center text-xs text-muted-foreground py-10 px-6" data-testid="thread-no-chat">
              No {(channelMeta[activeChannel] || {}).label || activeChannel} conversation found with this customer yet.
            </div>
          )}
          {needsThreadLoad && contactId && !liveThread?.loading && !liveThread?.conv && liveThread?.reason === "channel_not_configured" && (
            <div className="text-center text-xs text-muted-foreground py-10 px-6" data-testid="thread-not-configured">
              {(channelMeta[activeChannel] || {}).label || activeChannel} is not configured{activeChannel === "whatsapp" ? " with WATI" : " with PostProxy"}.
            </div>
          )}
          {needsThreadLoad && contactId && !liveThread?.loading && liveThread?.conv && shownMessages.length === 0 && (
            <div className="text-center text-xs text-muted-foreground py-10 px-6" data-testid="thread-empty">
              No {(channelMeta[activeChannel] || {}).label || activeChannel} messages with this customer yet — send a message{isWati ? " or template" : ""} below to start.
            </div>
          )}

          {!isEmailHistory && !needsThreadLoad && shownMessages.length === 0 && (
            <div className="text-center text-xs text-muted-foreground py-10">
              No messages on this channel yet. Start a conversation below.
            </div>
          )}
          {shownMessages.map((m) =>
            isEmailHistory
              ? <EmailCard key={m.id} m={m} contactName={conversation.contact_name} />
              : <Bubble key={m.id} m={m} />
          )}
          <div ref={endRef} />
        </div>
      </ScrollArea>

      {/* Composer */}
      {isEmailHistory ? (
        contactId ? (
          <div className="border-t border-border bg-card/40 p-3 space-y-2 shrink-0" data-testid="email-composer">
            {showBotDraft && (
              <div className="flex items-center gap-1.5 text-[11px] text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 rounded-md px-2 py-1" data-testid="bot-draft-banner">
                <Bot className="w-3.5 h-3.5" /> Bot-suggested draft — edit &amp; send
              </div>
            )}
            <input
              data-testid="email-subject-input"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Subject"
              className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <div className="flex items-end gap-2 min-w-0">
              <Textarea
                data-testid="email-body-textarea"
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Write your email reply…"
                className="min-h-[60px] max-h-40 resize-none flex-1 min-w-0"
              />
              <Button
                data-testid="email-send-btn"
                onClick={sendEmail}
                disabled={emailSending || !emailBody.trim()}
                size="icon"
                className="shrink-0 w-11 h-11 bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
                title="Send email"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <div className="text-[10px] text-muted-foreground">
              {emailSending ? "Sending…" : `Sends a real email via your configured SMTP server to ${conversation.contact_name}.`}
            </div>
          </div>
        ) : (
          <div className="border-t border-border bg-muted/30 p-3 shrink-0 text-center text-xs text-muted-foreground" data-testid="history-readonly-note">
            Save this caller as a CRM contact to reply by email.
          </div>
        )
      ) : readOnly ? (
        <div className="border-t border-border bg-muted/30 p-3 shrink-0 text-center text-xs text-muted-foreground" data-testid="history-readonly-note">
          Set up the {(channelMeta[activeChannel] || {}).label || activeChannel} handle to enable live chat.
        </div>
      ) : (
      <div className="border-t border-border bg-card/40 p-3 space-y-2.5 shrink-0">
        {showBotDraft && (
          <div className="flex items-center gap-1.5 text-[11px] text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 rounded-md px-2 py-1" data-testid="bot-draft-banner">
            <Bot className="w-3.5 h-3.5" /> Bot-suggested draft — edit &amp; send
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <QuickAction dataId="qa-apply-offer" icon={Tag} label="Apply Offer" color="text-primary" />
          <QuickAction dataId="qa-discount" icon={Percent} label="Discount" color="text-emerald-600" />
          <QuickAction dataId="qa-callback" icon={CalendarClock} label="Callback" color="text-accent" />
          <QuickAction dataId="qa-escalate" icon={AlertOctagon} label="Escalate" color="text-red-500" />
          {isWati && (
            <Button
              data-testid="wa-template-btn"
              variant="outline" size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() => setShowTemplate(true)}
            >
              <LayoutTemplate className="w-3.5 h-3.5" /> Template
            </Button>
          )}
          <Select value={tone} onValueChange={setTone}>
            <SelectTrigger data-testid="tone-select" className="ml-auto h-8 w-[130px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Friendly Tone">Friendly Tone</SelectItem>
              <SelectItem value="Formal Tone">Formal Tone</SelectItem>
              <SelectItem value="Sales Tone">Sales Tone</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2 min-w-0">
          <Button data-testid="attach-btn" variant="ghost" size="icon" className="shrink-0"><Paperclip className="w-4 h-4" /></Button>
          <Textarea
            data-testid="message-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={`Message via ${(channelMeta[activeChannel] || channelMeta.system).label}…`}
            className="min-h-[44px] max-h-32 resize-none flex-1 min-w-0"
          />
          <Button data-testid="send-message-btn" onClick={send} size="icon" className="shrink-0 w-11 h-11 bg-primary hover:bg-primary/90 text-primary-foreground" title="Send">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
      )}

      {showTemplate && (
        <WatiTemplateDialog
          conversationId={conversation.id}
          contactName={conversation.contact_name}
          onClose={() => setShowTemplate(false)}
          onSent={(data) => {
            setShowTemplate(false);
            toast.success("Template message sent");
            onSent?.(data?.message || { _refresh: true });
          }}
        />
      )}

      {showCreateLead && (
        <CreateLeadFromConvDialog
          conversation={conversation}
          onClose={() => setShowCreateLead(false)}
          onCreated={(contact) => {
            setShowCreateLead(false);
            toast.success(`Contact ${contact.full_name} added to CRM`);
            onSent?.({ _refresh: true });
          }}
        />
      )}
    </div>
  );
}


function WatiTemplateDialog({ conversationId, contactName, onClose, onSent }) {
  const [templateName, setTemplateName] = useState("");
  const [broadcastName, setBroadcastName] = useState("");
  const [params, setParams] = useState([]);   // [{name, value}]
  const [busy, setBusy] = useState(false);

  const addParam = () => setParams((p) => [...p, { name: String(p.length + 1), value: "" }]);
  const removeParam = (i) => setParams((p) => p.filter((_, idx) => idx !== i));
  const updateParam = (i, key, val) =>
    setParams((p) => p.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));

  async function submit() {
    if (!templateName.trim()) { toast.error("Template name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/me/whatsapp/send-template", {
        conversation_id: conversationId,
        template_name: templateName.trim(),
        broadcast_name: broadcastName.trim() || undefined,
        parameters: params.filter((p) => p.name && p.value),
      });
      onSent?.(data);
    } catch (e) {
      toast.error(formatErr(e) || "Failed to send template");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="wa-template-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LayoutTemplate className="w-4 h-4" /> Send WhatsApp template
          </DialogTitle>
          <DialogDescription>
            Use an approved WATI template to message {contactName || "this contact"} —
            works even outside the 24-hour session window.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium">Template name</label>
            <Input
              data-testid="wa-template-name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g. welcome_message"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Broadcast name (optional)</label>
            <Input
              data-testid="wa-template-broadcast"
              value={broadcastName}
              onChange={(e) => setBroadcastName(e.target.value)}
              placeholder="Defaults to the template name"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium">Parameters</label>
              <Button data-testid="wa-template-add-param" variant="ghost" size="sm"
                      className="h-7 gap-1 text-xs" onClick={addParam}>
                <Plus className="w-3.5 h-3.5" /> Add
              </Button>
            </div>
            {params.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  data-testid={`wa-template-param-name-${i}`}
                  className="w-20" value={p.name}
                  onChange={(e) => updateParam(i, "name", e.target.value)}
                  placeholder="name"
                />
                <Input
                  data-testid={`wa-template-param-value-${i}`}
                  className="flex-1" value={p.value}
                  onChange={(e) => updateParam(i, "value", e.target.value)}
                  placeholder="value"
                />
                <Button variant="ghost" size="icon" className="shrink-0"
                        onClick={() => removeParam(i)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid="wa-template-send" onClick={submit} disabled={busy} className="gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
